import { effect, inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ToastrService } from 'ngx-toastr';
import { from, of, timer, zip } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as fromGenerated from '../../_generated';
import { AnalyticsService } from '../../analytics';
import * as fromAssistants from '../assistants';
import {
  LOADING_TIME_FOR_MESSAGE,
  RECENT_CHATS_LIMIT,
} from '../chats.constants';
import { ChatsSocket } from '../chats.socket';
import * as fromShared from '../shared';
import {
  ChatActions,
  ChatsActions,
  ChatsSocketActions,
  ChatWithMessagesActions,
} from './chats.actions';
import { chatsFeature, ChatsState } from './chats.reducer';
import { isRenameRequestCurrent } from './chats.store.helper';

const DOCUMENT_CITATIONS_LOADING_TIMEOUT_MS = 20000;

@Injectable()
export class ChatsEffects {
  private readonly actions$ = inject(Actions);
  private readonly chatsService = inject(fromGenerated.ChatsService);
  private readonly documentsService = inject(fromGenerated.DocumentsService);
  private readonly store = inject(Store<ChatsState>);
  private readonly translateService = inject(TranslateService);
  private readonly routerService = inject(fromShared.RouterService);
  private readonly chatsSocket = inject(ChatsSocket);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly toastrService = inject(ToastrService);

  private readonly chats = this.store.selectSignal(chatsFeature.selectAll);

  constructor() {
    const selectedChatId = this.store.selectSignal(
      chatsFeature.selectSelectedChatId,
    );
    const hasAnEmptyChat = this.store.selectSignal(
      chatsFeature.selectHasAnEmptyChat,
    );
    /**
     * If no chat is selected, we need to select the empty chat.
     */
    effect(() => {
      this.chats();
      if (!selectedChatId() && hasAnEmptyChat()) {
        return this.store.dispatch(ChatActions.selectEmptyChat());
      }
    });
  }

  loadChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChats),
      exhaustMap(() =>
        this.chatsService.chatsControllerGetChatsV1(RECENT_CHATS_LIMIT).pipe(
          map((chats) => ChatsActions.loadChatsSuccess({ chats })),
          catchError((error: Error) =>
            of(ChatsActions.loadChatsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  loadChatsForAssistant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.loadChatsForAssistant),
      exhaustMap(({ assistantUuid }) =>
        this.chatsService
          .chatsControllerGetChatsV1(RECENT_CHATS_LIMIT, assistantUuid)
          .pipe(
            map((chats) =>
              ChatsActions.loadChatsForAssistantSuccess({ chats }),
            ),
            catchError((error: Error) =>
              of(
                ChatsActions.loadChatsForAssistantFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  selectChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.selectChat),
      map(({ id }) => {
        const selectedChat = this.store.selectSignal(
          chatsFeature.selectChatById(id),
        )();

        return ChatWithMessagesActions.loadChatWithMessages({
          id,
          // If the chat has messages, we don't need to display the loading state
          withoutIsLoading:
            selectedChat?.messages && selectedChat?.messages?.length > 0
              ? true
              : false,
        });
      }),
    ),
  );

  loadChatWithMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.loadChatWithMessages),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChat)),
      switchMap(([{ id }, chat]) => {
        /**
         * If the chat is empty, we don't need to load the messages
         */
        if (chat?._count.messages === 0) {
          return of(
            ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages: { ...chat, messages: chat.messages ?? [] },
            }),
          );
        }

        return zip(
          this.chatsService.chatsControllerGetChatWithMessagesV1(id),
          timer(LOADING_TIME_FOR_MESSAGE),
        ).pipe(
          map(([chatWithMessages]) => {
            return ChatWithMessagesActions.loadChatWithMessagesSuccess({
              chatWithMessages,
            });
          }),
          catchError((error: Error) =>
            of(
              ChatWithMessagesActions.loadChatWithMessagesFailure({
                error: error.message,
              }),
            ),
          ),
        );
      }),
    ),
  );

  loadChatWithMessagesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.loadChatWithMessagesFailure),
        switchMap(() => from(this.routerService.navigateToNotFound())),
      ),
    { dispatch: false },
  );

  addChatMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.addChatMessage),
      switchMap(({ chatId, content, assistant, documents }) => {
        const addChatMessageDto: fromGenerated.AddChatMessageDto = {
          content,
        };

        if (assistant) {
          addChatMessageDto.assistantUuid = assistant.uuid;
          addChatMessageDto.assistantLocale = assistant.locale;
        }
        if (documents) {
          addChatMessageDto.documentIds = documents.map(
            (document) => document.uuid,
          );
        }

        return this.chatsService
          .chatsControllerAddChatMessageV1(chatId, addChatMessageDto)
          .pipe(
            map((chatWithMessages) =>
              ChatWithMessagesActions.addChatMessageSuccess({
                chatWithMessages,
              }),
            ),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            catchError((error: any) =>
              of(
                ChatWithMessagesActions.addChatMessageFailure({
                  chatId,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  error:
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    error?.error?.name ??
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    error?.name ??
                    (this.translateService.instant(
                      'TOAST.ERROR.DEFAULT',
                    ) as string),
                }),
              ),
            ),
          );
      }),
    ),
  );

  loadEmptyChatIfNeeded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ChatWithMessagesActions.addChatMessageSuccess,
          ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess,
          ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure,
        ),
        tap(() => {
          if (!this.chats().find((chat) => chat?._count.messages === 0)) {
            this.store.dispatch(ChatsActions.loadChats());
          }
        }),
      ),
    { dispatch: false },
  );

  requestDocumentCitations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.requestDocumentCitations),
      withLatestFrom(this.store.select(chatsFeature.selectSelectedChatId)),
      filter(([_, selectedChatId]) => !!selectedChatId),
      switchMap(([{ chatMessageId, documentUuid }, selectedChatId]) => {
        return this.chatsService
          .chatsControllerGenerateDocumentCitationsV1(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedChatId!,
            chatMessageId,
            documentUuid,
          )
          .pipe(map(() => ({ chatMessageId, documentUuid, selectedChatId })));
      }),
      // Cancel other timers when clicking on another chip
      switchMap(({ chatMessageId, documentUuid, selectedChatId }) =>
        timer(DOCUMENT_CITATIONS_LOADING_TIMEOUT_MS).pipe(
          withLatestFrom(
            this.store.select(chatsFeature.selectIsCitationsLoading),
          ),
          map(([, isCitationsLoading]) => {
            if (isCitationsLoading) {
              Sentry.captureEvent({
                message: 'Document citations request timed out',
                level: 'error',
                extra: {
                  chatMessageId,
                  documentUuid,
                  selectedChatId,
                },
              });
            }
            return ChatWithMessagesActions.setCitationsLoading({
              isLoading: false,
            });
          }),
        ),
      ),
      catchError((error: Error) =>
        of(
          ChatWithMessagesActions.requestDocumentCitationsFailure({
            error: error.message,
          }),
        ),
      ),
    ),
  );

  requestQuickAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.requestQuickAction),
      switchMap(({ chatId, chatMessageId, quickAction, quickActionMessage }) =>
        this.chatsService
          .chatsControllerRequestQuickActionV1(chatId, chatMessageId, {
            quickAction,
            quickActionMessage,
          })
          .pipe(
            map(() =>
              ChatWithMessagesActions.requestQuickActionSuccess({
                chatId,
                quickAction,
              }),
            ),
            catchError((error: Error) =>
              of(
                ChatWithMessagesActions.requestQuickActionFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  deleteChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.deleteChat),
      switchMap(({ id }) =>
        this.chatsService.chatsControllerSoftDeleteChat0V1(id).pipe(
          map(() => ChatsActions.deleteChatSuccess({ id })),
          catchError((error: Error) =>
            of(ChatsActions.deleteChatFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  );

  renameChat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatsActions.renameChat),
      mergeMap(({ id, title, previousTitle, requestId, source }) =>
        this.chatsService.chatsControllerRenameChatV1(id, { title }).pipe(
          map(() =>
            ChatsActions.renameChatSuccess({ id, title, requestId, source }),
          ),
          catchError((error: Error) =>
            // Sampled before the failure action dispatches: a newer rename of
            // the same chat overwrites the active requestId, so a mismatch
            // means this failure is stale (the reducer ignores it too).
            this.store.select(chatsFeature.selectActiveRenameRequests).pipe(
              take(1),
              map((activeRenameRequests) =>
                ChatsActions.renameChatFailure({
                  id,
                  title,
                  previousTitle,
                  requestId,
                  error: error.message,
                  superseded: !isRenameRequestCurrent(
                    activeRenameRequests,
                    id,
                    requestId,
                  ),
                }),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  renameChatFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.renameChatFailure),
        filter(({ superseded }) => !superseded),
        tap(() => {
          this.toastrService.error(
            this.translateService.instant(
              'CHAT.RENAME_CHAT_MODAL.ERROR',
            ) as string,
            undefined,
            { closeButton: true, disableTimeOut: true },
          );
        }),
      ),
    { dispatch: false },
  );

  sendChatRenamedAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsActions.renameChatSuccess),
        mergeMap(({ source, title }) =>
          this.analyticsService.sendChatRenamedEvent({
            source,
            titleLength: title.length,
          }),
        ),
      ),
    { dispatch: false },
  );

  onAssistantDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAssistants.AssistantActions.deleteAssistantSuccess),
      withLatestFrom(this.store.select(chatsFeature.selectAll)),
      map(([{ uuid }, chats]) => {
        const chatIdsToRemove = chats
          .filter((chat) => chat.assistantId === uuid)
          .map((chat) => chat.id);
        return ChatsActions.deleteManyChatsSuccess({ ids: chatIdsToRemove });
      }),
    ),
  );

  chatsSocketConnect = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatsSocketActions.reconnect),
        tap(() => {
          this.chatsSocket.reconnect();
        }),
      ),
    { dispatch: false },
  );

  addDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatWithMessagesActions.addDocuments),
      mergeMap(
        ({ transactionId, chatId, assistantUuid, files, documentRole }) => {
          const payload: fromGenerated.CreateDocumentFromChatDto = {
            transactionId,
            chatId,
            documentsToCreate: files.map((file) => ({
              documentId: file.uuid,
              filename: file.name,
              ...(documentRole ? { role: documentRole } : {}),
            })),
          };

          if (assistantUuid) {
            payload.assistantUuid = assistantUuid;
          }

          return this.documentsService
            .documentsControllerCreateDocumentsFromChatV1(payload)
            .pipe(
              map((documents) =>
                ChatWithMessagesActions.addDocumentsSuccess({
                  chatId,
                  documents,
                }),
              ),
              catchError((error: Error) =>
                of(
                  ChatWithMessagesActions.addDocumentsFailure({
                    chatId,
                    documentUuids: files.map((file) => file.uuid),
                    error: error.message,
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );

  sendMessageSentAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.addChatMessageSuccess),
        switchMap(({ chatWithMessages }) =>
          this.analyticsService.sendMessageSentEvent(chatWithMessages),
        ),
      ),
    { dispatch: false },
  );

  sendQuickActionSentAnalyticsEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatWithMessagesActions.requestQuickActionSuccess),
        switchMap(({ chatId, quickAction }) => {
          const selectedChat = this.store.selectSignal(
            chatsFeature.selectChatById(chatId),
          )();

          if (!selectedChat) {
            return of(null);
          }

          return this.analyticsService.sendQuickActionSentEvent(
            {
              ...selectedChat,
              messages: selectedChat.messages ?? [],
            },
            quickAction,
          );
        }),
      ),
    { dispatch: false },
  );
}
